import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import logoImage from '../assets/strom2_webb.png'
import { apiFetch, authClient, authService, clearLegacyAuthStorage } from '../services/auth'

type LoginStep = 'email' | 'otp'

type PersistedLoginState = {
  email: string
  step: LoginStep
}

const loginStateStorageKey = 'strom2-login-state'

function readPersistedLoginState(): PersistedLoginState | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.sessionStorage.getItem(loginStateStorageKey)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as Partial<PersistedLoginState>
    if (typeof parsed.email !== 'string' || (parsed.step !== 'email' && parsed.step !== 'otp')) {
      return null
    }

    return {
      email: parsed.email,
      step: parsed.step,
    }
  } catch {
    return null
  }
}

function writePersistedLoginState(state: PersistedLoginState | null) {
  if (typeof window === 'undefined') {
    return
  }

  if (!state) {
    window.sessionStorage.removeItem(loginStateStorageKey)
    return
  }

  window.sessionStorage.setItem(loginStateStorageKey, JSON.stringify(state))
}

interface User {
  id: string
  email: string
  name: string
  displayName?: string
  givenName?: string
  surname?: string
  jobTitle?: string
  mobilePhone?: string
  businessPhones?: string[]
  officeLocation?: string
  preferredLanguage?: string
  userPrincipalName?: string
  role: string
  provider?: string
  createdAt?: string
  updatedAt?: string
  picture?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isInLobby: boolean
  isLoading: boolean
  getToken: () => Promise<string | null>
  login: (email: string) => Promise<void>
  verifyLogin: (email: string, otp: string) => Promise<void>
  logout: () => Promise<void>
  refreshUserProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

type SessionUser = typeof authClient.$Infer.Session.user

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

async function fetchProfile(): Promise<User | null> {
  const response = await apiFetch('/user/profile')

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) return null
    throw new Error('Profile service temporarily unavailable')
  }

  return await response.json() as User
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const sessionState = authClient.useSession()
  const [sessionResolved, setSessionResolved] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const activeSessionUserIdRef = useRef<string | null>(null)
  const profileRequestIdRef = useRef(0)
  const currentUserRef = useRef<User | null>(null)
  currentUserRef.current = user

  useEffect(() => {
    clearLegacyAuthStorage()
  }, [])

  useEffect(() => {
    if (!sessionState.isPending) {
      setSessionResolved(true)
    }
  }, [sessionState.isPending])

  const clearProfileState = useCallback(() => {
    profileRequestIdRef.current += 1
    activeSessionUserIdRef.current = null
    setProfileLoading(false)
    setUser(null)
  }, [])

  const refreshUserProfile = useCallback(async (sessionUserOverride?: SessionUser | null) => {
    const sessionUser = sessionUserOverride ?? sessionState.data?.user ?? null
    if (!sessionUser) {
      clearProfileState()
      return
    }

    const requestId = ++profileRequestIdRef.current
    const sameSession = activeSessionUserIdRef.current === sessionUser.id
    if (!sameSession) setUser(null)
    activeSessionUserIdRef.current = sessionUser.id
    setProfileLoading(true)
    try {
      const profile = await fetchProfile()
      if (profileRequestIdRef.current !== requestId || activeSessionUserIdRef.current !== sessionUser.id) {
        return
      }
      setUser(profile)
    } catch {
      if (profileRequestIdRef.current === requestId && (!sameSession || !currentUserRef.current)) {
        setUser(null)
      }
    } finally {
      if (profileRequestIdRef.current === requestId) {
        setProfileLoading(false)
      }
    }
  }, [clearProfileState, sessionState.data?.user?.id])

  useEffect(() => {
    const sessionUser = sessionState.data?.user ?? null

    if (sessionState.isPending && !sessionUser) {
      return
    }

    if (!sessionUser) {
      clearProfileState()
      return
    }

    void refreshUserProfile(sessionUser)
  }, [clearProfileState, refreshUserProfile, sessionState.data, sessionState.data?.user?.id, sessionState.isPending])

  const login = async (email: string) => {
    await authService.sendLoginCode(email)
  }

  const getToken = async () => 'cookie-session'

  const verifyLogin = async (email: string, otp: string) => {
    await authService.verifyLoginCode(email, otp)
    await sessionState.refetch()
  }

  const logout = async () => {
    await authService.logout()
    clearProfileState()
    writePersistedLoginState(null)
  }

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: !!user && user.role !== 'lobby',
    isInLobby: user?.role === 'lobby',
    // A background session/profile refresh must not unmount the dashboard and
    // discard forms. Initial loads still block; 401/403 still clear the user.
    isLoading: !sessionResolved || (Boolean(sessionState.data?.user) && profileLoading && !user),
    getToken,
    login,
    verifyLogin,
    logout,
    refreshUserProfile,
  }), [getToken, login, logout, profileLoading, refreshUserProfile, sessionResolved, sessionState.data?.user, user, verifyLogin])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function LoginPage() {
  const { login, verifyLogin } = useAuth()
  const [email, setEmail] = useState(() => readPersistedLoginState()?.email ?? '')
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState<LoginStep>(() => readPersistedLoginState()?.step ?? 'email')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      writePersistedLoginState(null)
      return
    }

    writePersistedLoginState({
      email: trimmedEmail,
      step,
    })
  }, [email, step])

  const handleEmailChange = (value: string) => {
    setEmail(value)

    if (step === 'otp') {
      setStep('email')
      setOtp('')
    }
  }

  const handleSendCode = async () => {
    try {
      setLoading(true)
      setError(null)
      await login(email.trim())
      setStep('otp')
    } catch {
      setError('Kunde inte skicka koden. Kontrollera e-postadressen.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    try {
      setLoading(true)
      setError(null)
      await verifyLogin(email.trim(), otp.trim())
      writePersistedLoginState(null)
    } catch {
      setError('Fel kod eller utgången kod.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        padding: '56px 44px',
        maxWidth: '440px',
        width: '100%',
        margin: '20px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={logoImage} alt="Ström2 Webb" style={{ height: '72px', width: 'auto', marginBottom: '16px' }} />
          <h1 style={{ margin: 0, fontSize: '30px', color: '#1a202c' }}>Välkommen till Ström2</h1>
          <p style={{ color: '#718096', marginBottom: 0 }}>Logga in med e-post och engångskod</p>
        </div>

        {error && (
          <div style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '12px 14px', borderRadius: '10px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: '10px', color: '#334155', fontSize: '14px' }}>E-post</label>
        <input
          value={email}
          onChange={(event) => handleEmailChange(event.target.value)}
          type="email"
          autoComplete="email"
          placeholder="namn@strom2.se"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 16px',
            borderRadius: '10px',
            border: '1px solid #cbd5e1',
            marginBottom: '14px',
          }}
        />

        {step === 'otp' && (
          <>
            <label style={{ display: 'block', marginBottom: '10px', color: '#334155', fontSize: '14px' }}>Engångskod</label>
            <input
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '14px 16px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                marginBottom: '14px',
              }}
            />
          </>
        )}

        <button
          onClick={step === 'email' ? handleSendCode : handleVerify}
          disabled={loading || !email.trim() || (step === 'otp' && !otp.trim())}
          style={{
            width: '100%',
            padding: '14px 18px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: loading ? '#93c5fd' : '#2563eb',
            color: 'white',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Väntar...' : step === 'email' ? 'Skicka kod' : 'Logga in'}
        </button>
      </div>
    </div>
  )
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <div>Laddar...</div>
      </div>
    )
  }

  if (!user) {
    return <LoginPage />
  }

  return <>{children}</>
}
